import { isAxiosError } from "axios";
import { ZodError } from "zod";

const GATEWAY_FRIENDLY_MESSAGE =
  "We're having trouble reaching the server right now. Please try again in a few minutes.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function looksLikeHtml(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const head = value.trimStart().slice(0, 100).toLowerCase();
  return (
    head.startsWith("<!doctype") ||
    head.startsWith("<html") ||
    head.includes("<body")
  );
}

function hasHtmlContentType(headers: unknown): boolean {
  if (!isRecord(headers)) return false;
  const contentType = headers["content-type"];
  return typeof contentType === "string" && contentType.startsWith("text/html");
}

function firstStringFromUnknown(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim().length > 0) return value;
  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = firstStringFromUnknown(entry);
      if (found) return found;
    }
  }
  if (isRecord(value)) {
    for (const v of Object.values(value)) {
      const found = firstStringFromUnknown(v);
      if (found) return found;
    }
  }
  return undefined;
}

function extractFromResponseData(data: unknown): string | undefined {
  if (!isRecord(data)) return firstStringFromUnknown(data);

  // Common API shapes
  const message = data.message;
  if (typeof message === "string" && message.trim().length > 0) return message;

  const error = data.error;
  if (typeof error === "string" && error.trim().length > 0) return error;

  const detail = data.detail;
  if (typeof detail === "string" && detail.trim().length > 0) return detail;

  // FastAPI-style validation: { detail: [{ loc, msg, type }] }
  if (Array.isArray(detail)) {
    const msg = detail.find(isRecord)?.msg;
    if (typeof msg === "string" && msg.trim().length > 0) return msg;
  }

  // Common DRF validation style
  const nonFieldErrors = data.non_field_errors;
  const nonField = firstStringFromUnknown(nonFieldErrors);
  if (nonField) return nonField;

  // Generic field-errors object: { field: ["msg"] }
  return firstStringFromUnknown(data);
}

const MAX_MESSAGE_LENGTH = 70;

function truncate(msg: string): string {
  return msg.length > MAX_MESSAGE_LENGTH
    ? `${msg.slice(0, MAX_MESSAGE_LENGTH)}...`
    : msg;
}

function formatZodError(error: ZodError): string {
  const issues = error.issues.map((i) => {
    const path = i.path.length > 0 ? `${i.path.join(".")}: ` : "";
    return `${path}${i.message}`;
  });
  return truncate(issues.join("; "));
}

export function getBackendErrorMessage(error: unknown): string {
  if (error instanceof ZodError) {
    return formatZodError(error);
  }

  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 502 || status === 504) return GATEWAY_FRIENDLY_MESSAGE;

    const data: unknown = error.response?.data;
    if (looksLikeHtml(data) || hasHtmlContentType(error.response?.headers)) {
      return GATEWAY_FRIENDLY_MESSAGE;
    }

    const fromResponse = extractFromResponseData(data);
    if (fromResponse) return fromResponse;

    if (typeof status === "number" && status >= 500) {
      return GATEWAY_FRIENDLY_MESSAGE;
    }

    if (typeof error.message === "string" && error.message.trim().length > 0) {
      return error.message;
    }
  }

  if (error instanceof Error) return error.message;
  const asString = firstStringFromUnknown(error);
  return asString ?? "An unknown error occurred";
}

export function formatBackendError(prefix: string, error: unknown): string {
  const message = getBackendErrorMessage(error);
  return prefix ? `${prefix}: ${message}` : message;
}

export function isNotFoundError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

export function isForbiddenError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 403;
}

/** True when the JWT client's token refresh itself failed, i.e. the session has expired. */
export function isTokenRefreshError(error: unknown): boolean {
  return isAxiosError(error) && !!error.config?.url?.includes("/token/refresh");
}
